import React, { useEffect, useState } from 'react';
import { statsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import DateRangePicker from '../components/DateRangePicker';
import ClientFilter from '../components/ClientFilter';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

interface Stats {
  totalTickets: number;
  newTickets: number;
  assignedTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  avgFirstResponseTime: number;
  avgResolutionTime: number;
}

interface SLAStats {
  totalTickets: number;
  firstResponse: {
    avgTime: number;
    breachRate: number;
    complianceRate: number;
    totalWithResponse: number;
    fastestResponse: number;
    slowestResponse: number;
    target: number;
  };
  resolution: {
    avgTime: number;
    breachRate: number;
    complianceRate: number;
    totalResolved: number;
    fastestResolution: number;
    slowestResolution: number;
    target: number;
  };
}

const StatsPage: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [slaStats, setSlaStats] = useState<SLAStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedClient, setSelectedClient] = useState<string | null>(null);

  useEffect(() => {
    // Добавляем debounce для предотвращения слишком частых запросов
    const timeoutId = setTimeout(() => {
      fetchStats();
    }, 500); // Задержка 500мс

    return () => clearTimeout(timeoutId);
  }, [startDate, endDate, selectedClient]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      console.log('Начинаю загрузку статистики для периода:', startDate.toLocaleDateString(), '-', endDate.toLocaleDateString(), selectedClient ? `и клиента: ${selectedClient}` : '');
      
      // Проверяем токен
      const token = localStorage.getItem('token');
      console.log('Токен в localStorage:', token ? 'Есть' : 'Отсутствует');
      
      const [statsResponse, slaResponse] = await Promise.all([
        statsApi.getOverview({ startDate, endDate }, selectedClient || undefined),
        statsApi.getSLA({ startDate, endDate }, selectedClient || undefined),
      ]);
      
      console.log('Stats response:', statsResponse);
      console.log('SLA response:', slaResponse);
      
      // Упрощенная обработка данных - берем данные напрямую из response.data
      let statsData = null;
      let slaData = null;
      
      if (statsResponse.data && statsResponse.data.success) {
        // Проверяем разные возможные структуры ответа
        if (statsResponse.data.data && statsResponse.data.data.overview) {
          statsData = statsResponse.data.data.overview;
        } else if (statsResponse.data.data) {
          statsData = statsResponse.data.data;
        } else {
          statsData = statsResponse.data;
        }
      }
      
      if (slaResponse.data && slaResponse.data.success) {
        slaData = slaResponse.data.data || slaResponse.data;
      }
      
      console.log('Извлеченные stats данные:', statsData);
      console.log('Извлеченные SLA данные:', slaData);
      
      // Обрабатываем статистику
      if (statsData && typeof statsData === 'object') {
        const processedStats = {
          totalTickets: Number(statsData.totalTickets) || 0,
          newTickets: Number(statsData.newTickets) || 0,
          assignedTickets: Number(statsData.assignedTickets) || 0,
          inProgressTickets: Number(statsData.inProgressTickets) || 0,
          resolvedTickets: Number(statsData.resolvedTickets) || 0,
          avgFirstResponseTime: Number(statsData.avgFirstResponseTime) || 0,
          avgResolutionTime: Number(statsData.avgResolutionTime) || 0,
        };
        console.log('Обработанная статистика:', processedStats);
        setStats(processedStats);
      } else {
        console.warn('Stats данные не найдены или имеют неправильную структуру:', statsData);
        setStats({
          totalTickets: 0,
          newTickets: 0,
          assignedTickets: 0,
          inProgressTickets: 0,
          resolvedTickets: 0,
          avgFirstResponseTime: 0,
          avgResolutionTime: 0,
        });
      }
      
      // Обрабатываем SLA данные
      if (slaData && typeof slaData === 'object') {
        const processedSLA = {
          totalTickets: Number(slaData.totalTickets) || 0,
          firstResponse: {
            avgTime: Number(slaData.firstResponse?.avgTime) || 0,
            breachRate: Number(slaData.firstResponse?.breachRate) || 0,
            complianceRate: Number(slaData.firstResponse?.complianceRate) || 100,
            totalWithResponse: Number(slaData.firstResponse?.totalWithResponse) || 0,
            fastestResponse: Number(slaData.firstResponse?.fastestResponse) || 0,
            slowestResponse: Number(slaData.firstResponse?.slowestResponse) || 0,
            target: Number(slaData.firstResponse?.target) || 5,
          },
          resolution: {
            avgTime: Number(slaData.resolution?.avgTime) || 0,
            breachRate: Number(slaData.resolution?.breachRate) || 0,
            complianceRate: Number(slaData.resolution?.complianceRate) || 100,
            totalResolved: Number(slaData.resolution?.totalResolved) || 0,
            fastestResolution: Number(slaData.resolution?.fastestResolution) || 0,
            slowestResolution: Number(slaData.resolution?.slowestResolution) || 0,
            target: Number(slaData.resolution?.target) || 60,
          },
        };
        console.log('Обработанные SLA данные:', processedSLA);
        setSlaStats(processedSLA);
      } else {
        console.warn('SLA данные не найдены или имеют неправильную структуру:', slaData);
        setSlaStats({
          totalTickets: 0,
          firstResponse: {
            avgTime: 0,
            breachRate: 0,
            complianceRate: 100,
            totalWithResponse: 0,
            fastestResponse: 0,
            slowestResponse: 0,
            target: 5,
          },
          resolution: {
            avgTime: 0,
            breachRate: 0,
            complianceRate: 100,
            totalResolved: 0,
            fastestResolution: 0,
            slowestResolution: 0,
            target: 60,
          },
        });
      }
      
      console.log('Статистика успешно загружена');
    } catch (error: any) {
      console.error('Ошибка загрузки статистики:', error);
      console.error('Детали ошибки:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      
      if (error.response?.status === 401) {
        toast.error('Необходима авторизация');
      } else if (error.response?.status === 500) {
        toast.error('Ошибка сервера при загрузке статистики');
      } else if (error.code === 'ECONNREFUSED') {
        toast.error('Сервер недоступен');
      } else {
        toast.error(`Ошибка загрузки статистики: ${error.message}`);
      }
      
      // Устанавливаем пустые данные при ошибке
      setStats({
        totalTickets: 0,
        newTickets: 0,
        assignedTickets: 0,
        inProgressTickets: 0,
        resolvedTickets: 0,
        avgFirstResponseTime: 0,
        avgResolutionTime: 0,
      });
      
      setSlaStats({
        totalTickets: 0,
        firstResponse: {
          avgTime: 0,
          breachRate: 0,
          complianceRate: 100,
          totalWithResponse: 0,
          fastestResponse: 0,
          slowestResponse: 0,
          target: 5,
        },
        resolution: {
          avgTime: 0,
          breachRate: 0,
          complianceRate: 100,
          totalResolved: 0,
          fastestResolution: 0,
          slowestResolution: 0,
          target: 60,
        },
      });
    } finally {
      setLoading(false);
    }
  };


  const formatTime = (minutes: number | undefined | null) => {
    if (minutes === undefined || minutes === null) return 'N/A';
    if (minutes === 0) return '0 мин';
    if (minutes < 60) return `${Math.round(minutes)} мин`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}ч ${remainingMinutes}м`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Статистика</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Аналитика работы системы и SLA метрики
            {selectedClient && (
              <span className="ml-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Фильтр по клиенту активен
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
          <div className="flex items-center space-x-2">
            <Button
              onClick={fetchStats}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              {loading ? 'Обновление...' : 'Обновить'}
            </Button>
          </div>
          <ClientFilter
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            className="mr-0 sm:mr-3"
          />
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </div>
      </div>

      {/* Общая статистика */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2">
        <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
          <div className="p-3 sm:p-4 md:p-5">
            <h3 className="text-base sm:text-lg font-medium text-foreground">Всего заявок</h3>
            <p className="mt-2 text-2xl sm:text-3xl font-bold text-primary">
              {stats?.totalTickets || 0}
            </p>
          </div>
        </div>

        <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
          <div className="p-5">
            <h3 className="text-lg font-medium text-foreground">Среднее время первого ответа</h3>
            <p className="mt-2 text-3xl font-bold text-primary">
              {formatTime(stats?.avgFirstResponseTime)}
            </p>
          </div>
        </div>
      </div>

      {/* Статистика по статусам */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-5 sm:grid-cols-2 md:grid-cols-4">
        <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
          <div className="p-5">
            <h3 className="text-sm font-medium text-muted-foreground">Новые</h3>
            <p className="mt-2 text-2xl font-bold text-warning-500">
              {stats?.newTickets || 0}
            </p>
          </div>
        </div>
        <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
          <div className="p-5">
            <h3 className="text-sm font-medium text-muted-foreground">Назначенные</h3>
            <p className="mt-2 text-2xl font-bold text-primary">
              {stats?.assignedTickets || 0}
            </p>
          </div>
        </div>
        <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
          <div className="p-5">
            <h3 className="text-sm font-medium text-muted-foreground">В работе</h3>
            <p className="mt-2 text-2xl font-bold text-primary">
              {stats?.inProgressTickets || 0}
            </p>
          </div>
        </div>
        <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
          <div className="p-5">
            <h3 className="text-sm font-medium text-muted-foreground">Решенные</h3>
            <p className="mt-2 text-2xl font-bold text-success-500">
              {stats?.resolvedTickets || 0}
            </p>
          </div>
        </div>
      </div>

      {/* SLA Метрики */}
      {slaStats && (
        <div className="space-y-5">
          <h2 className="text-xl font-semibold text-foreground">
            SLA Метрики
          </h2>
          
          {/* Информационное сообщение когда нет данных */}
          {slaStats.firstResponse?.totalWithResponse === 0 && slaStats.resolution?.totalResolved === 0 && (
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-5 h-5 text-primary">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 text-sm text-foreground">
                  <p>
                    <strong>Информация:</strong> В выбранном периоде нет заявок с ответами операторов или решениями. 
                    SLA метрики будут доступны после обработки заявок операторами.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
              <div className="p-5">
                <h3 className="text-lg font-medium text-foreground">
                  Первый ответ
                </h3>
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Цель:</span>
                    <span className="font-medium text-foreground">{slaStats.firstResponse?.target || 5} мин</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Среднее время:</span>
                    <span className="font-medium text-foreground">
                      {slaStats.firstResponse?.totalWithResponse > 0 
                        ? formatTime(slaStats.firstResponse?.avgTime)
                        : 'Нет данных'
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Самый быстрый ответ:</span>
                    <span className="font-medium text-foreground">
                      {slaStats.firstResponse?.totalWithResponse > 0
                        ? formatTime(slaStats.firstResponse?.fastestResponse)
                        : 'Нет данных'
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Самый долгий ответ:</span>
                    <span className="font-medium text-foreground">
                      {slaStats.firstResponse?.totalWithResponse > 0
                        ? formatTime(slaStats.firstResponse?.slowestResponse)
                        : 'Нет данных'
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Соблюдение SLA:</span>
                    <span className={`font-medium ${
                      (slaStats.firstResponse?.complianceRate || 0) >= 90 ? 'text-success-500' :
                      (slaStats.firstResponse?.complianceRate || 0) >= 70 ? 'text-warning-500' : 'text-destructive'
                    }`}>
                      {slaStats.firstResponse?.complianceRate || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
              <div className="p-5">
                <h3 className="text-lg font-medium text-foreground">
                  Решение
                </h3>
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Цель:</span>
                    <span className="font-medium text-foreground">{slaStats.resolution?.target || 60} мин</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Среднее время:</span>
                    <span className="font-medium text-foreground">
                      {slaStats.resolution?.totalResolved > 0 
                        ? formatTime(slaStats.resolution?.avgTime)
                        : 'Нет данных'
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Самое быстрое решение:</span>
                    <span className="font-medium text-foreground">
                      {slaStats.resolution?.totalResolved > 0
                        ? formatTime(slaStats.resolution?.fastestResolution)
                        : 'Нет данных'
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Самое долгое решение:</span>
                    <span className="font-medium text-foreground">
                      {slaStats.resolution?.totalResolved > 0
                        ? formatTime(slaStats.resolution?.slowestResolution)
                        : 'Нет данных'
                      }
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Соблюдение SLA:</span>
                    <span className={`font-medium ${
                      (slaStats.resolution?.complianceRate || 0) >= 90 ? 'text-success-500' :
                      (slaStats.resolution?.complianceRate || 0) >= 70 ? 'text-warning-500' : 'text-destructive'
                    }`}>
                      {slaStats.resolution?.complianceRate || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Сводка SLA */}
          <div className="overflow-hidden shadow rounded-lg bg-card border border-border">
            <div className="p-5">
              <h3 className="text-lg font-medium text-foreground">
                Сводка SLA
              </h3>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className={`text-2xl font-bold ${
                    (slaStats.firstResponse?.complianceRate || 0) >= 90 ? 'text-success-500' :
                    (slaStats.firstResponse?.complianceRate || 0) >= 70 ? 'text-warning-500' : 'text-destructive'
                  }`}>
                    {slaStats.firstResponse?.complianceRate || 0}%
                  </div>
                  <div className="text-sm text-muted-foreground">Соблюдение первого ответа</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${
                    (slaStats.resolution?.complianceRate || 0) >= 90 ? 'text-success-500' :
                    (slaStats.resolution?.complianceRate || 0) >= 70 ? 'text-warning-500' : 'text-destructive'
                  }`}>
                    {slaStats.resolution?.complianceRate || 0}%
                  </div>
                  <div className="text-sm text-muted-foreground">Соблюдение решения</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsPage;